'use client'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import type { Settings } from 'react-slick'
import '@/styles/vendors/slickCarousel'

import { cn } from '@/utils/twMerge'
import { PrismicRichText } from '@prismicio/react'
import { sliderSettingsWithoutDot } from '@/utils/sliderSettings'
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next'

// Lazy load Slider to reduce initial bundle size
const Slider = dynamic<Settings>(() => import('react-slick'), {
  ssr: false,
  loading: () => <div className="h-64 animate-pulse rounded-md bg-gray-100" />,
})

type ItemObject = Record<string, any>
type DataObject = Record<string, any>

type E2EComplianceComponentProps = {
  data: DataObject
  type?: string
  uid?: string
}

const E2EComplianceComponent = ({ data, type, uid }: E2EComplianceComponentProps) => {
  const getGradientStyle = (type?: string): string => {
    if (type === 'whyE2ECompliance') {
      return '[&>h2]:from-secondary-blue-100 [&>h2]:to-primary-ink-100'
    }
    if (['companySmallE2ECompliance', 'framework'].includes(type || '')) {
      return '[&>h2]:from-secondary-purple-100 [&>h2]:to-primary-ink-100'
    }
    return '[&>h2]:from-primary-green-85 [&>h2]:to-primary-ink-100'
  }

  const isHomepage = type === 'homepageE2ECompliance'

  return (
    <section
      className={cn(
        'relative z-[1] py-20 lg:py-[140px]',
        ['framework', 'whyE2ECompliance'].includes(type as string) && '!pt-0',
      )}>
      <div className="container">
        <div
          className={cn(
            '[&>h2]:text-shadow-[0px_0px_#00000000] mx-auto [&>h2]:mb-10 [&>h2]:bg-gradient-to-r [&>h2]:bg-clip-text [&>h2]:pb-3 [&>h2]:text-transparent [&>h2]:lg:leading-[64px] [&>h2_em]:block [&>h2_em]:not-italic [&>h2_em]:text-primary-ink-100 [&>h2_em]:lg:-mt-3',
            getGradientStyle(type),
            isHomepage ? 'max-w-3xl' : 'max-w-2xl',
          )}>
          <PrismicRichText field={data.primary.title} />
          <PrismicRichText field={data.primary.description} />
        </div>

        {/* Desktop version */}
        <div className="mt-56 hidden lg:grid lg:grid-cols-2 lg:gap-8">
          {data.items.map((item: ItemObject, index: number) => (
            <div
              className="relative rounded-md bg-primary-ink-100 px-[42px] py-10 [&>h3]:mb-2 [&>h3]:text-white [&>p]:min-h-[120px] [&>p]:text-white/60"
              key={`i-${index}`}>
              {/* Logo: aspect ratio by context — homepage 487/350 for full-width; company-small/others 487/271 to avoid bottom empty space */}
              <div
                className={cn('relative -mt-40 w-full max-w-[487px]', isHomepage ? 'aspect-[487/350]' : 'aspect-[487/271]')}
              >
                <PrismicNextImage
                  field={item.logo}
                  fill
                  className="object-contain object-left-top"
                  sizes="(max-width: 1024px) 100vw, 487px"
                  fallbackAlt=""
                />
              </div>
              {/* Homepage pulls the wordmark into the taller mockup box; other pages keep mt-12 so screenshots cannot cover it */}
              <div className={cn('relative mb-8 w-full max-w-[280px] aspect-[280/31]', isHomepage ? '-mt-10' : 'mt-4')}>
                <PrismicNextImage field={item.icon} fill className="object-contain object-left-top" sizes="280px" fallbackAlt="" />
              </div>
              <PrismicRichText
                field={item.title}
                components={{
                  // Section title is h2 — use h3 for a11y, keep former h4 visual size
                  heading4: ({ children }) => (
                    <h3 className="text-xl font-normal tracking-[-0.45px] xl:text-3xl">{children}</h3>
                  ),
                }}
              />
              <PrismicRichText field={item.description} />

              <PrismicNextLink field={item.btn_link} className="btn btn-default btn-white btn-interaction">
                {item.btn_label}
                <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
              </PrismicNextLink>
            </div>
          ))}
        </div>

        {/* Mobile version */}
        <div className="mt-10 block lg:hidden [&_.slick-slider]:-mx-4 [&_.slick-slider]:my-0 [&_.slick-slider_.slick-slide]:px-3">
          <Slider {...sliderSettingsWithoutDot}>
            {data.items.map((item: ItemObject, index: number) => (
              <div
                className="relative rounded-md bg-primary-ink-100 p-8 lg:px-[42px] lg:py-10 [&>h3]:mb-2 [&>h3]:text-white [&>p]:min-h-[150px] [&>p]:text-white/60"
                key={`i-${index}`}>
                <PrismicNextImage field={item.icon} className="mb-8" fallbackAlt="" />
                <PrismicRichText
                  field={item.title}
                  components={{
                    heading4: ({ children }) => (
                      <h3 className="text-xl font-normal tracking-[-0.45px] xl:text-3xl">{children}</h3>
                    ),
                  }}
                />
                <PrismicRichText field={item.description} />

                <PrismicNextLink field={item.btn_link} className="btn btn-default btn-white btn-interaction">
                  {item.btn_label}
                  <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
                </PrismicNextLink>
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </section>
  )
}

export default E2EComplianceComponent
